/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { pollingService } from '../services/polling.service'
import {
  type RepeatFunction,
  type StopPollingFunction,
  type PollingOptions,
  type UsePollingReturn,
  type UseRepeatUntilReturn,
  type UseRepeatUntilConfig
} from '../types/polling'

/**
 * Hook for managing polling operations in React components
 * Provides start/stop functionality with automatic cleanup on unmount
 *
 * @param options - Optional default polling configuration
 * @returns Polling control interface
 */
export function usePolling(options?: PollingOptions): UsePollingReturn {
  const [isPolling, setIsPolling] = useState(false)
  const [iterationCount, setIterationCount] = useState(0)
  const stopFunctionRef = useRef<StopPollingFunction | null>(null)
  const currentRepeatFnRef = useRef<RepeatFunction | null>(null)

  const startPolling = useCallback(
    (repeatFn: RepeatFunction, pollOptions?: PollingOptions) => {
      // Stop existing polling if running
      if (stopFunctionRef.current) {
        stopFunctionRef.current()
      }

      // Reset iteration count
      setIterationCount(0)
      currentRepeatFnRef.current = repeatFn

      // Wrap repeat function to track iterations
      const wrappedRepeatFn = () => {
        const result = repeatFn()
        setIterationCount((prev) => prev + 1)

        // Handle async functions
        if (result && typeof result.then === 'function') {
          return result
        }
        return result
      }

      // Merge options
      const finalOptions = { ...options, ...pollOptions }

      // Start polling
      const stopFn = pollingService.repeatUntil(wrappedRepeatFn, finalOptions)
      stopFunctionRef.current = stopFn
      setIsPolling(true)
    },
    [options]
  )

  const stopPolling = useCallback(() => {
    if (stopFunctionRef.current) {
      stopFunctionRef.current()
      stopFunctionRef.current = null
      setIsPolling(false)
      currentRepeatFnRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  return {
    startPolling,
    stopPolling,
    isPolling,
    iterationCount
  }
}

/**
 * Hook for a specific repeat operation with configuration
 * More declarative approach with auto-start option
 *
 * @param config - Configuration including the repeat function
 * @returns Repeat operation control interface
 */
export function useRepeatUntil(config: UseRepeatUntilConfig): UseRepeatUntilReturn {
  const [isRunning, setIsRunning] = useState(false)
  const [iterationCount, setIterationCount] = useState(0)
  const [error, setError] = useState<Error | null>(null)
  const stopFunctionRef = useRef<StopPollingFunction | null>(null)
  const isRunningRef = useRef(false)

  const start = useCallback(() => {
    if (isRunningRef.current) return

    isRunningRef.current = true
    setError(null)
    setIterationCount(0)

    // Wrap repeat function with error handling and iteration tracking
    const wrappedRepeatFn = () => {
      try {
        const result = config.repeatFn()
        setIterationCount((prev) => prev + 1)
        setError(null)

        // Handle async functions
        if (result && typeof result.then === 'function') {
          return result.catch((err: Error | unknown) => {
            const errorObj = err instanceof Error ? err : new Error(String(err))
            setError(errorObj)
            throw errorObj // Re-throw to let polling service handle it
          })
        }

        return result
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err))
        setError(errorObj)
        throw errorObj // Re-throw to let polling service handle it
      }
    }

    // Configure options with error handler
    const options: PollingOptions = {
      ...config,
      onError: (err) => {
        setError(err)
        if (config.onError) {
          config.onError(err)
        }
      }
    }

    // Start polling
    const stopFn = pollingService.repeatUntil(wrappedRepeatFn, options)
    stopFunctionRef.current = stopFn
    setIsRunning(true)
  }, [config, isRunning])

  const stop = useCallback(() => {
    if (stopFunctionRef.current) {
      stopFunctionRef.current()
      stopFunctionRef.current = null
      isRunningRef.current = false
      setIsRunning(false)
    }
  }, [])

  // Auto-start if configured
  useEffect(() => {
    if (config.autoStart && !isRunning) {
      start()
    }
  }, [config.autoStart, isRunning, start])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return {
    start,
    stop,
    isRunning,
    iterationCount,
    error
  }
}

/**
 * Hook for simple interval-based operations
 * Simplified version of usePolling for basic repeated operations
 *
 * @param callback - Function to execute repeatedly
 * @param interval - Interval in milliseconds (null to disable)
 * @param immediate - Whether to execute immediately
 * @returns Control interface
 */
export function useInterval(
  callback: RepeatFunction,
  interval: number | null,
  immediate: boolean = false
): { isRunning: boolean; stop: () => void } {
  const [isRunning, setIsRunning] = useState(false)
  const stopFunctionRef = useRef<StopPollingFunction | null>(null)
  const callbackRef = useRef(callback)

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // Start/stop interval based on interval parameter
  useEffect(() => {
    if (interval !== null && interval > 0) {
      const stopFn = pollingService.repeatUntil(() => callbackRef.current(), { interval, immediate })
      stopFunctionRef.current = stopFn
      setIsRunning(true)

      return () => {
        stopFn()
        setIsRunning(false)
      }
    } else {
      if (stopFunctionRef.current) {
        stopFunctionRef.current()
        stopFunctionRef.current = null
        setIsRunning(false)
      }
    }
  }, [interval, immediate])

  const stop = useCallback(() => {
    if (stopFunctionRef.current) {
      stopFunctionRef.current()
      stopFunctionRef.current = null
      setIsRunning(false)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return { isRunning, stop }
}

/**
 * Hook for one-time delayed execution
 * React equivalent of setTimeout with cleanup
 *
 * @param callback - Function to execute
 * @param delay - Delay in milliseconds (null to disable)
 * @returns Control interface
 */
export function useTimeout(callback: RepeatFunction, delay: number | null): { isPending: boolean; cancel: () => void } {
  const [isPending, setIsPending] = useState(false)
  const cancelFunctionRef = useRef<StopPollingFunction | null>(null)
  const callbackRef = useRef(callback)

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // Start/cancel timeout based on delay parameter
  useEffect(() => {
    if (delay !== null && delay > 0) {
      setIsPending(true)

      const cancelFn = pollingService.delayed(() => {
        callbackRef.current()
        setIsPending(false)
      }, delay)

      cancelFunctionRef.current = cancelFn

      return () => {
        cancelFn()
        setIsPending(false)
      }
    } else {
      if (cancelFunctionRef.current) {
        cancelFunctionRef.current()
        cancelFunctionRef.current = null
        setIsPending(false)
      }
    }
  }, [delay])

  const cancel = useCallback(() => {
    if (cancelFunctionRef.current) {
      cancelFunctionRef.current()
      cancelFunctionRef.current = null
      setIsPending(false)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel()
    }
  }, [cancel])

  return { isPending, cancel }
}
